import React from 'react';

interface TermsAndConditionsProps {
  appName?: string;
  onClose: () => void;
}

const TermsAndConditions3DPrintForge: React.FC<TermsAndConditionsProps> = ({ appName = '3D Print Forge', onClose }) => {
  return (
    <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-[310] size-14 bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all group"
      >
        <span className="material-symbols-outlined transform group-hover:rotate-90 transition-transform">close</span>
      </button>

      <div className="max-w-[900px] mx-auto px-6 py-32 text-white/80">
        <h1 className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-none mb-4 italic text-white flex flex-col gap-2">
          <span>Terms &</span>
          <span>Conditions</span>
        </h1>
        <p className="text-xl text-primary font-bold tracking-widest uppercase mb-4">
          {appName}
        </p>
        <p className="text-sm font-bold tracking-widest uppercase mb-12 border-b border-white/10 pb-6 text-white/40">
          Effective date: 28 July 2026
        </p>

        <div className="space-y-12 font-light leading-relaxed text-lg">
          <section className="space-y-4">
            <p>
              These Terms and Conditions ("Terms") govern access to and use of {appName} (the "Service"), a print farm and print shop management platform provided by <strong className="text-white">T.H.E Apps</strong>, of 94 9th Street, Parkmore ("T.H.E Apps", "we", "us", "our"). By creating a workspace, signing in, or otherwise using the Service, you ("you", "your", "Customer") agree to be bound by these Terms. If you do not agree, do not use the Service.
            </p>
            <p>
              {appName} is a business tool intended for use by 3D printing businesses, print farms, and their staff to manage quoting, orders, invoicing, inventory, and production. It is not intended for personal or household use.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">1. The Service</h2>
            <p>
              {appName} provides tools for job costing, catalogue management, customer relationship management, quoting, order and invoice tracking, inventory management, and print production tracking, accessed via a workspace tied to your business ("Workspace"). Features available to your Workspace depend on your subscription tier as described in Section 3.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">2. Accounts and Workspaces</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">2.1 Registration</h3>
                <p>
                  You must register an account using a valid email address, or via Google sign-in, to use the Service. You are responsible for the accuracy of the information you provide and for maintaining the confidentiality of your login credentials.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">2.2 Workspace Ownership and Team Members</h3>
                <p>
                  Each Workspace has one owner ("Workspace Owner"), who is responsible for the Workspace's subscription and billing. The Workspace Owner may invite additional team members to join the Workspace via an invite code. Team members can access and act within the Workspace according to the permissions granted to them by the Workspace Owner. The Workspace Owner is responsible for the conduct of team members using the Workspace and for ensuring team members are authorized to access the business and customer data contained within it.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">2.3 Your Customer Data</h3>
                <p>
                  As part of using the Service, you may input, upload, or store information relating to your own customers (names, contact details, orders, quotes, invoices, and related records) ("Customer Data"). As between you and T.H.E Apps, you remain the owner and data controller of your Customer Data. T.H.E Apps processes Customer Data solely to provide the Service to you, in accordance with our Privacy Policy, available within the Service and at our website. You are responsible for ensuring you have the necessary rights and legal basis to input your customers' personal information into the Service, and for responding to any requests from your customers regarding their data.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">3. Subscription Plans and Billing</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.1 Free Evaluation Period</h3>
                <p>
                  Every new Workspace receives a 14-day Pro evaluation period with full access to Service features, with no payment card required at signup.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.2 Free and Pro Plans</h3>
                <p>
                  After the evaluation period ends, Workspaces that have not subscribed continue on the <strong className="text-white">Free</strong> plan, which provides unlimited customers, materials, and catalogue items, and up to five (5) new quotes per calendar month. The <strong className="text-white">Pro</strong> plan removes these limits and unlocks the full quote-to-order-to-production-to-invoice workflow. Current plan pricing is displayed within the Service prior to upgrade.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.3 Billing</h3>
                <p>
                  {appName} serves customers internationally. Pro subscriptions are billed in <strong className="text-white">South African Rand (ZAR) regardless of your location</strong>, and processed through our third-party payment processor, Paystack; your bank or card provider may apply its own currency conversion and any associated fees. Subscriptions automatically renew at the end of each billing period unless canceled beforehand. We do not store your payment card details; these are collected and processed directly by Paystack in accordance with its own terms and security standards.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.4 Canceling and Managing Your Subscription</h3>
                <p>
                  The Workspace Owner may manage or cancel the subscription at any time via the billing management link available within the Service, which is provided through Paystack's subscription management portal. Canceling stops future renewals; access to Pro features continues until the end of the then-current paid billing period, after which the Workspace reverts to the Free plan.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.5 Downgrades and Data Retention</h3>
                <p>
                  If a Workspace downgrades from Pro to Free (whether by cancellation or non-renewal), historical data created while on Pro (quotes, orders, invoices, customer records, catalogue, and inventory history) remains visible and accessible, subject to the operational limits of the Free plan. Creation of new quotes, orders, and invoices beyond Free plan limits will require re-subscribing to Pro.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.6 Refunds</h3>
                <p>
                  Refunds, where applicable, are handled in accordance with Paystack's applicable payment processing terms and any refund request should be directed to us at <a href="mailto:rob@theapps.io" className="text-primary hover:underline">rob@theapps.io</a> in the first instance so we can assist.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white/90 mb-2">3.7 Price Changes</h3>
                <p>
                  We may change subscription pricing from time to time. Any price change will take effect no earlier than your next billing renewal following the change, and we will make reasonable efforts to notify the Workspace Owner in advance.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">4. Acceptable Use</h2>
            <p className="mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any unlawful purpose or in a manner that violates these Terms;</li>
              <li>Attempt to gain unauthorized access to another Workspace, account, or any data not belonging to you;</li>
              <li>Interfere with or disrupt the integrity, security, or performance of the Service or its underlying infrastructure;</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the Service, except where permitted by law;</li>
              <li>Use the Service to store or transmit unlawful, infringing, or harmful content;</li>
              <li>Circumvent, disable, or otherwise interfere with any subscription, entitlement, or usage-limit mechanism within the Service.</li>
            </ul>
            <p className="mt-4">
              We reserve the right to suspend or terminate Workspaces that violate these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">5. Intellectual Property</h2>
            <p>
              The Service, including its software, design, branding, and underlying technology, is owned by T.H.E Apps and protected by applicable intellectual property laws. Subject to these Terms, we grant you a limited, non-exclusive, non-transferable right to access and use the Service for your business's internal operations. No other rights are granted. You retain all rights to your Customer Data and any content you input into the Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">6. Service Availability</h2>
            <p>
              We aim to keep the Service available and performant but do not guarantee uninterrupted or error-free operation. We may perform maintenance, updates, or changes to the Service from time to time, which may result in temporary unavailability. We recommend maintaining your own records or exports of critical business data as good practice.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">7. Disclaimers</h2>
            <p>
              The Service is provided "as is" and "as available," without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. Cost calculations, quotes, and other outputs generated by the Service are tools to assist your business decisions and do not constitute financial, tax, or legal advice; you remain responsible for verifying figures before relying on them commercially.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">8. Limitation of Liability</h2>
            <div className="space-y-4">
              <p>
                To the maximum extent permitted by law, T.H.E Apps shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, revenue, or profits, arising out of or related to your use of the Service, even if advised of the possibility of such damages. Our total liability for any claim arising from these Terms or the Service shall not exceed the total subscription fees you paid us in the twelve (12) months preceding the claim.
              </p>
              <p>
                Nothing in these Terms limits liability that cannot be limited under applicable law, including liability that cannot be excluded under South African consumer protection law where it applies to your use of the Service.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">9. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless T.H.E Apps and its officers, employees, and affiliates from any claims, damages, losses, or expenses (including reasonable legal fees) arising from your use of the Service, your violation of these Terms, your Customer Data, or your violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">10. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service for conduct that violates these Terms or is otherwise harmful to the Service or other users, with notice where reasonably practicable. You may stop using the Service and request closure of your Workspace at any time by contacting us at <a href="mailto:rob@theapps.io" className="text-primary hover:underline">rob@theapps.io</a>.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">11. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. Material changes will be communicated via the Service or by updating the effective date above. Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">12. Governing Law</h2>
            <p>
              {appName} is operated by T.H.E Apps, based in South Africa, and these Terms are governed by the laws of the Republic of South Africa, without regard to conflict of law principles. <strong className="text-white">This does not remove any mandatory consumer protection rights you have under the law of your own country or state of residence</strong> — where those rights cannot lawfully be waived or overridden (for example, under EU/UK consumer protection law or applicable US state law), they continue to apply to you alongside these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">13. Contact Us</h2>
            <p className="mb-4">
              Questions about these Terms can be directed to:
            </p>
            <div className="bg-white/5 p-6 border border-white/10 rounded-sm">
              <strong className="text-white text-lg block mb-2">T.H.E Apps</strong>
              <p className="mb-2">94 9th Street, Parkmore</p>
              <a href="mailto:rob@theapps.io" className="text-primary hover:underline">rob@theapps.io</a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions3DPrintForge;
